import { useState, useCallback } from 'react';
import { QuotationRoomDistributionFilter } from '../../../types';

interface UseRoomDistributionReturn {
	roomDistributions: QuotationRoomDistributionFilter[];
	setRoomDistributions: (rooms: QuotationRoomDistributionFilter[]) => void;
	handleRoomNumberChange: (value: number) => void;
	handleAdultsChange: (value: number, index: number) => void;
	handleChildrenChange: (value: number | null, index: number) => void;
	handleInfantsChange: (value: number | null, index: number) => void;
	clearRooms: () => void;
}

export const useRoomDistribution = (
	initialRooms: QuotationRoomDistributionFilter[] = [],
): UseRoomDistributionReturn => {
	const [roomDistributions, setRoomDistributions] = useState<QuotationRoomDistributionFilter[]>(initialRooms);

	const createDefaultRoom = useCallback(
		(): QuotationRoomDistributionFilter => ({
			totalAdults: 1,
			totalChildren: 0,
			totalInfants: 0,
		}),
		[],
	);

	const handleRoomNumberChange = useCallback(
		(value: number) => {
			if (value === 0) {
				setRoomDistributions([]);
				return;
			}

			setRoomDistributions((prev) => {
				if (value < prev.length) {
					// Reducir habitaciones
					return prev.slice(0, value);
				} else if (value > prev.length) {
					// Agregar habitaciones
					const newRooms = Array.from({ length: value - prev.length }, () => createDefaultRoom());
					return [...prev, ...newRooms];
				}
				return prev;
			});
		},
		[createDefaultRoom],
	);

	const handleAdultsChange = useCallback((value: number, index: number) => {
		setRoomDistributions((prev) => {
			if (index >= prev.length) return prev;
			const newRooms = [...prev];
			newRooms[index] = { ...newRooms[index], totalAdults: value };
			return newRooms;
		});
	}, []);

	const handleChildrenChange = useCallback((value: number | null, index: number) => {
		setRoomDistributions((prev) => {
			if (index >= prev.length) return prev;
			const newRooms = [...prev];
			newRooms[index] = { ...newRooms[index], totalChildren: value };
			return newRooms;
		});
	}, []);

	const handleInfantsChange = useCallback((value: number | null, index: number) => {
		setRoomDistributions((prev) => {
			if (index >= prev.length) return prev;
			const newRooms = [...prev];
			newRooms[index] = { ...newRooms[index], totalInfants: value };
			return newRooms;
		});
	}, []);

	const clearRooms = useCallback(() => {
		setRoomDistributions([]);
	}, []);

	return {
		roomDistributions,
		setRoomDistributions,
		handleRoomNumberChange,
		handleAdultsChange,
		handleChildrenChange,
		handleInfantsChange,
		clearRooms,
	};
};
